import React, { useState } from 'react';
import { GameOutput, PlayOutput, Player, PlayerWithLeagueContext, LeagueOutput } from '../../generated-api';
import { XMarkIcon, FunnelIcon } from '@heroicons/react/24/outline';

interface PlayFilterWidgetProps {
  games: GameOutput[];
  players: (Player | PlayerWithLeagueContext)[];
  plays: PlayOutput[];
  leagues?: LeagueOutput[];
  selectedGame: string;
  selectedPlayerCount: string;
  selectedPlayers: string[];
  selectedLeagues?: string[];
  onSelectedGameChange: (gameId: string) => void;
  onSelectedPlayerCountChange: (count: string) => void;
  onSelectedPlayersChange: (playerIds: string[]) => void;
  onSelectedLeaguesChange?: (leagueIds: string[]) => void;
  onClearFilters: () => void;
  filteredPlaysCount: number;
  renderFilterButton?: (props: { onClick: () => void; isExpanded: boolean; hasActiveFilters: boolean }) => React.ReactNode;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
}

const PlayFilterWidget: React.FC<PlayFilterWidgetProps> = ({
  games,
  players,
  plays,
  leagues = [],
  selectedGame,
  selectedPlayerCount,
  selectedPlayers,
  selectedLeagues = [],
  onSelectedGameChange,
  onSelectedPlayerCountChange,
  onSelectedPlayersChange,
  onSelectedLeaguesChange,
  onClearFilters,
  filteredPlaysCount,
  renderFilterButton,
  isExpanded: controlledIsExpanded,
  onToggleExpanded,
}) => {
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  const isExpanded: boolean = controlledIsExpanded !== undefined ? controlledIsExpanded : internalIsExpanded;

  const getPlayerCountForPlay = (play: PlayOutput): number => {
    return Object.values(play.teams).flat().length;
  };

  const getUniquePlayerCounts = (): number[] => {
    const counts = plays.map(getPlayerCountForPlay);
    const uniqueCounts = Array.from(new Set(counts));
    return uniqueCounts.sort((a, b) => a - b);
  };

  const getGameName = (gameId: string) => {
    const game = games.find(g => g.id === gameId);
    return game ? game.name : `Game ${gameId.slice(-4)}`;
  };

  const hasActiveFilters: boolean = !!(selectedGame || selectedPlayerCount || selectedPlayers.length > 0 || selectedLeagues.length > 0);

  const toggleExpanded = () => {
    if (onToggleExpanded) {
      onToggleExpanded();
    } else {
      setInternalIsExpanded(!internalIsExpanded);
    }
  };

  // If renderFilterButton is provided, don't render the default button
  const defaultButton = !renderFilterButton ? (
    <div className="md:hidden mb-4">
      <button
        onClick={toggleExpanded}
        className={`w-full flex items-center justify-between px-4 py-3 border rounded-md transition-colors ${
          hasActiveFilters
            ? 'bg-primary-50 border-primary-300 text-primary-700'
            : 'bg-surface border-edge-input text-body hover:bg-surface-hover'
        }`}
      >
        <div className="flex items-center space-x-2">
          <FunnelIcon className="h-5 w-5" />
          <span className="font-medium">Filters</span>
          {hasActiveFilters && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-700 text-white">
              Active
            </span>
          )}
        </div>
        <svg
          className={`h-5 w-5 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    </div>
  ) : null;

  return (
    <>
      {renderFilterButton && renderFilterButton({ onClick: toggleExpanded, isExpanded, hasActiveFilters })}
      <div className={`mb-6 bg-surface shadow rounded-lg p-6 ${isExpanded ? 'block' : 'hidden'} md:block`}>
        {defaultButton}

      {/* Desktop Header */}
      <h3 className="hidden md:block text-lg font-medium text-heading mb-4">Filters</h3>
      
      {/* Filter Content - Collapsible on mobile, always visible on desktop */}
      <div className={`${isExpanded ? 'block' : 'hidden'} md:block transition-all duration-200`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Game Filter */}
        <div>
          <label htmlFor="game-filter" className="block text-sm font-medium text-body mb-2">
            Filter by Game
          </label>
          <select
            id="game-filter"
            value={selectedGame}
            onChange={(e) => onSelectedGameChange(e.target.value)}
            className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Games</option>
            {games
              .filter(game => plays.some(play => play.gameId === game.id))
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(game => (
                <option key={game.id} value={game.id}>
                  {game.name}
                </option>
              ))
            }
          </select>
        </div>

        {/* Player Count Filter */}
        <div>
          <label htmlFor="player-count-filter" className="block text-sm font-medium text-body mb-2">
            Filter by Player Count
          </label>
          <select
            id="player-count-filter"
            value={selectedPlayerCount}
            onChange={(e) => onSelectedPlayerCountChange(e.target.value)}
            className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Player Counts</option>
            {getUniquePlayerCounts().map(count => (
              <option key={count} value={count.toString()}>
                {count} {count === 1 ? 'Player' : 'Players'}
              </option>
            ))}
          </select>
        </div>

        {/* Players Filter */}
        <div>
          <label htmlFor="players-filter" className="block text-sm font-medium text-body mb-2">
            Filter by Players (must include all selected)
          </label>
          <div className="relative">
            <div className="border border-edge-input rounded-md px-3 py-2 min-h-[38px] bg-surface focus-within:ring-primary-500 focus-within:border-primary-500">
              {selectedPlayers.length === 0 ? (
                <span className="text-disabled text-sm">Select players...</span>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {selectedPlayers.map(playerId => {
                    const player = players.find(p => p.id === playerId);
                    return (
                      <span
                        key={playerId}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800"
                      >
                        {player?.name || 'Unknown Player'}
                        <button
                          data-track="play_filter_player"
                          type="button"
                          onClick={() => onSelectedPlayersChange(selectedPlayers.filter(id => id !== playerId))}
                          className="ml-1 text-primary-700 hover:text-primary-800"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <select
              id="players-filter"
              value=""
              onChange={(e) => {
                if (e.target.value && !selectedPlayers.includes(e.target.value)) {
                  onSelectedPlayersChange([...selectedPlayers, e.target.value]);
                }
              }}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            >
              <option value="">Add a player...</option>
              {players
                .filter(player => 
                  plays.some(play => Object.values(play.teams).flat().includes(player.id)) &&
                  !selectedPlayers.includes(player.id)
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(player => (
                  <option key={player.id} value={player.id}>
                    {player.name}
                  </option>
                ))
              }
            </select>
          </div>
          {selectedPlayers.length > 0 && (
            <p className="text-xs text-muted mt-1">
              Shows plays where ALL selected players participated
            </p>
          )}
        </div>

        {/* Leagues Filter */}
        {onSelectedLeaguesChange && (
          <div>
            <label htmlFor="leagues-filter" className="block text-sm font-medium text-body mb-2">
              Filter by League (any selected)
            </label>
            <div className="relative">
              <div className="border border-edge-input rounded-md px-3 py-2 min-h-[38px] bg-surface focus-within:ring-primary-500 focus-within:border-primary-500">
                {selectedLeagues.length === 0 ? (
                  <span className="text-disabled text-sm">Select leagues...</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {selectedLeagues.map(leagueId => {
                      const league = leagues.find(l => l.id === leagueId);
                      return (
                        <span
                          key={leagueId}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                        >
                          {league?.name || 'Unknown League'}
                          <button
                            data-track="play_filter_league"
                            type="button"
                            onClick={() => onSelectedLeaguesChange(selectedLeagues.filter(id => id !== leagueId))}
                            className="ml-1 text-purple-600 hover:text-purple-800"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <select
                id="leagues-filter"
                value=""
                onChange={(e) => {
                  if (e.target.value && !selectedLeagues.includes(e.target.value)) {
                    onSelectedLeaguesChange([...selectedLeagues, e.target.value]);
                  }
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              >
                <option value="">Add a league...</option>
                {leagues
                  .filter(league => 
                    plays.some(play => play.leagueIds.includes(league.id)) &&
                    !selectedLeagues.includes(league.id)
                  )
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(league => (
                    <option key={league.id} value={league.id}>
                      {league.name}
                    </option>
                  ))
                }
              </select>
            </div>
            {selectedLeagues.length > 0 && (
              <p className="text-xs text-muted mt-1">
                Shows plays associated with ANY selected league
              </p>
            )}
          </div>
        )}
      </div>
      </div>

      {/* Filter Results Summary - Collapsible on mobile, always visible on desktop */}
      <div className={`${isExpanded ? 'block' : 'hidden'} md:block mt-4 pt-4 border-t border-edge flex flex-col md:flex-row justify-between items-start md:items-center gap-2 md:gap-0`}>
        <p className="text-sm text-body">
          Showing {filteredPlaysCount} of {plays.length} play sessions
          {selectedGame && (
            <span className="ml-1">
              for <strong>{getGameName(selectedGame)}</strong>
            </span>
          )}
          {selectedPlayerCount && (
            <span className="ml-1">
              with <strong>{selectedPlayerCount} {selectedPlayerCount === '1' ? 'player' : 'players'}</strong>
            </span>
          )}
          {selectedPlayers.length > 0 && (
            <span className="ml-1">
              including <strong>
                {selectedPlayers.map(playerId => {
                  const player = players.find(p => p.id === playerId);
                  return player?.name || 'Unknown Player';
                }).join(', ')}
              </strong>
            </span>
          )}
          {selectedLeagues.length > 0 && (
            <span className="ml-1">
              in leagues <strong>
                {selectedLeagues.map(leagueId => {
                  const league = leagues.find(l => l.id === leagueId);
                  return league?.name || 'Unknown League';
                }).join(', ')}
              </strong>
            </span>
          )}
        </p>
        {(selectedGame || selectedPlayerCount || selectedPlayers.length > 0 || selectedLeagues.length > 0) && (
          <button
            onClick={onClearFilters}
            className="text-sm text-primary-700 hover:text-primary-800 font-medium"
          >
            Clear Filters
          </button>
        )}
      </div>
    </div>
    </>
  );
};

export default PlayFilterWidget;
